import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from "react-router-dom";
import { XCircle, Loader2, Home, Mail, CheckCircle, Clock, Shield, Smartphone } from "lucide-react";
import { authApi } from "../../services/auth.api";
import { useAuthStore } from "../../store/authStore";
import { ApiError } from "../../services/api";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

interface InvitePreview {
  inviterName: string;
  homeName: string;
  email: string;
  expiresAt: string;
  action: "signin" | "signup";
}

type PageState = "loading" | "preview" | "otp" | "success" | "error";

function daysUntil(isoDate: string) {
  const ms = new Date(isoDate).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

export function AcceptInvitePage() {
  const { t } = useTranslation('family');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  const token = searchParams.get("token") ?? "";

  const [pageState, setPageState] = useState<PageState>("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const [preview, setPreview] = useState<InvitePreview | null>(null);

  // OTP / invite-auth state
  const [inviteAuthData, setInviteAuthData] = useState<{
    email: string;
    isNewUser: boolean;
    inviterName: string;
    homeName: string;
  } | null>(null);
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [otpError, setOtpError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(30);
  const resendTimer = useRef<NodeJS.Timeout | null>(null);

  // ── Step 1: load public preview ────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setErrorMsg("No invite token provided.");
      setPageState("error");
      return;
    }

    fetch(`${API_BASE}/v1/invite/preview?token=${encodeURIComponent(token)}`)
      .then((res) => res.json())
      .then((json) => {
        if (json.success) {
          setPreview(json.data as InvitePreview);
          setPageState("preview");
        } else {
          setErrorMsg(json.message || "This invite is invalid or has already been used.");
          setPageState("error");
        }
      })
      .catch(() => {
        setErrorMsg("Could not load invite. Please check your connection and try again.");
        setPageState("error");
      });
  }, [token]);

  // ── Resend countdown ───────────────────────────────────────────────────
  const startResendCountdown = () => {
    setResendSeconds(30);
    if (resendTimer.current) clearInterval(resendTimer.current);
    resendTimer.current = setInterval(() => {
      setResendSeconds((s) => {
        if (s <= 1) {
          clearInterval(resendTimer.current!);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  useEffect(() => () => { if (resendTimer.current) clearInterval(resendTimer.current); }, []);

  // ── Step 2: call invite-auth → send OTP ───────────────────────────────
  const handleContinue = async () => {
    setIsSubmitting(true);
    setOtpError("");
    try {
      const data = await authApi.inviteAuth(token);
      setInviteAuthData(data);
      setPageState("otp");
      startResendCountdown();
    } catch (err) {
      setOtpError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Step 3: verify OTP → accept invite → login ─────────────────────────
  const handleVerify = async () => {
    if (otp.length !== 6) { setOtpError("Please enter the full 6-digit code."); return; }
    if (inviteAuthData?.isNewUser && !name.trim()) {
      setOtpError("Please enter your name to create your account.");
      return;
    }
    setIsSubmitting(true);
    setOtpError("");
    try {
      const result = await authApi.verifyInviteOtp({
        token,
        otp,
        name: inviteAuthData?.isNewUser ? name.trim() : undefined,
      });
      setAuth(result.user, result.accessToken, result.refreshToken);
      setPageState("success");
      setTimeout(() => navigate("/dashboard", { replace: true }), 2000);
    } catch (err) {
      setOtp("");
      setOtpError(err instanceof ApiError ? err.message : "Invalid code. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Resend OTP ─────────────────────────────────────────────────────────
  const handleResend = async () => {
    if (resendSeconds > 0) return;
    try {
      await authApi.resendInviteOtp(token);
      setOtp("");
      setOtpError("");
      startResendCountdown();
    } catch {
      setOtpError("Failed to resend code. Please try again.");
    }
  };

  const daysLeft = preview ? daysUntil(preview.expiresAt) : 0;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        {/* ── Loading ─────────────────────────────────────────────── */}
        {pageState === "loading" && (
          <div className="text-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-4" />
            <p className="text-sm text-slate-500">{t('acceptInvite.loading')}</p>
          </div>
        )}

        {/* ── Error ────────────────────────────────────────────────── */}
        {pageState === "error" && (
          <div className="rounded-2xl bg-white shadow-md border border-slate-200 p-8 text-center space-y-4">
            <div className="flex justify-center">
              <XCircle className="h-14 w-14 text-red-500" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">{t('acceptInvite.errorTitle')}</h2>
              <p className="text-sm text-slate-500 mt-1">{errorMsg}</p>
            </div>
            <p className="text-xs text-slate-400">
              {t('acceptInvite.errorHint')}
            </p>
          </div>
        )}

        {/* ── Invite Preview Card ──────────────────────────────────── */}
        {pageState === "preview" && preview && (
          <div className="rounded-2xl bg-white shadow-md border border-slate-200 overflow-hidden">
            {/* Header banner */}
            <div className="bg-[#1e3a5f] px-6 py-5 text-center">
              <p className="text-sm font-medium text-blue-200 tracking-widest uppercase">
                BrandSmart
              </p>
              <p className="text-xs text-blue-300 mt-0.5">{t('acceptInvite.familyInvitation')}</p>
            </div>

            <div className="p-6 space-y-5">
              {/* Headline */}
              <div className="text-center space-y-1">
                <h2 className="text-lg font-semibold text-slate-900">{t('acceptInvite.hiThere')}</h2>
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-800">{preview.inviterName}</span> has
                  invited you to join{" "}
                  <span className="font-semibold text-slate-800">{preview.homeName}</span> on
                  BrandSmart.
                </p>
              </div>

              {/* Details row */}
              <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <Shield className="w-3.5 h-3.5" />
                  {t('acceptInvite.fullAccess')}
                </span>
                <span className="text-slate-300">·</span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  Expires in {daysLeft} day{daysLeft === 1 ? "" : "s"}
                </span>
              </div>

              {/* CTA — primary: open native app (if installed) */}
              <a
                href={`homeiq://accept-invite?token=${encodeURIComponent(token)}`}
                className="w-full py-3 px-4 rounded-xl bg-[#1e3a5f] text-white text-sm font-semibold
                  hover:bg-[#162d4e] active:scale-[0.98] transition-all flex items-center justify-center gap-2 no-underline"
              >
                <Smartphone className="h-4 w-4" />
                {t('acceptInvite.openApp')}
              </a>

              {/* Divider */}
              <div className="flex items-center gap-3 text-xs text-slate-400">
                <span className="flex-1 border-t border-slate-200" />
                <span>{t('acceptInvite.orContinueBrowser')}</span>
                <span className="flex-1 border-t border-slate-200" />
              </div>

              {/* Secondary: web OTP flow */}
              <button
                onClick={handleContinue}
                disabled={isSubmitting}
                className="w-full py-3 px-4 rounded-xl border border-slate-300 text-slate-700 text-sm font-medium
                  hover:bg-slate-50 active:scale-[0.98] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> {t('acceptInvite.sendingCode')}</>
                ) : (
                  t('acceptInvite.acceptViaEmail')
                )}
              </button>

              {/* Invited email note */}
              <p className="text-xs text-center text-slate-400">
                {t('acceptInvite.verificationSentTo')}{" "}
                <span className="font-medium text-slate-600">{preview.email}</span>
              </p>
            </div>
          </div>
        )}

        {/* ── OTP Verification ────────────────────────────────────── */}
        {pageState === "otp" && inviteAuthData && (
          <div className="rounded-2xl bg-white shadow-md border border-slate-200 p-6 space-y-5">
            {/* Icon + title */}
            <div className="text-center space-y-2">
              <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto">
                <Mail className="w-6 h-6 text-[#1e3a5f]" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900">{t('acceptInvite.checkInbox')}</h2>
              <p className="text-sm text-slate-500">
                {t('acceptInvite.sentCodeTo')}{" "}
                <span className="font-medium text-slate-700">{inviteAuthData.email}</span>
              </p>
            </div>

            {/* Name field — only for new accounts */}
            {inviteAuthData.isNewUser && (
              <div className="space-y-1">
                <label htmlFor="invite-name" className="text-xs font-medium text-slate-700">{t('acceptInvite.yourFullName')}</label>
                <input
                  id="invite-name"
                  type="text"
                  placeholder={t('acceptInvite.firstLastName')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full h-11 rounded-xl border border-slate-200 px-4 text-sm
                    focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 focus:border-[#1e3a5f]"
                />
              </div>
            )}

            {/* OTP Input */}
            <div className="space-y-1">
                <label htmlFor="invite-otp" className="text-xs font-medium text-slate-700">{t('acceptInvite.verificationCode')}</label>
              <input
                id="invite-otp"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={otp}
                onChange={(e) => { setOtp(e.target.value.replace(/\D/g, "").slice(0, 6)); setOtpError(""); }}
                className="w-full h-14 rounded-xl border border-slate-200 px-4 text-center text-2xl
                  tracking-[0.4em] font-mono focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20
                  focus:border-[#1e3a5f]"
                autoFocus
              />
              {otpError && (
                <p className="text-xs text-red-500 px-1">{otpError}</p>
              )}
            </div>

            {/* Verify button */}
            <button
              onClick={handleVerify}
              disabled={isSubmitting || otp.length !== 6}
              className="w-full py-3 px-4 rounded-xl bg-[#1e3a5f] text-white text-sm font-semibold
                hover:bg-[#162d4e] active:scale-[0.98] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {(() => {
                if (isSubmitting) return <><Loader2 className="h-4 w-4 animate-spin" /> Verifying…</>;
                if (inviteAuthData.isNewUser) return "Create Account & Accept";
                return "Verify & Accept Invitation";
              })()}
            </button>

            {/* Resend */}
            <p className="text-xs text-center text-slate-500">
              {t('auth.otp.didntReceive')}{" "}
              {resendSeconds > 0 ? (
                <span className="text-slate-400">{t('acceptInvite.resendIn', { n: resendSeconds })}</span>
              ) : (
                <button onClick={handleResend} className="text-[#1e3a5f] font-medium underline underline-offset-2">
                  {t('acceptInvite.resend')}
                </button>
              )}
            </p>
          </div>
        )}

        {/* ── Success ──────────────────────────────────────────────── */}
        {pageState === "success" && (
          <div className="rounded-2xl bg-white shadow-md border border-slate-200 p-8 text-center space-y-4">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900">{t('acceptInvite.welcomeTitle')}</h2>
              <p className="text-sm text-slate-500 mt-1">
                Welcome to{" "}
                <span className="font-medium">{inviteAuthData?.homeName ?? "the home"}</span>.
                Redirecting you now…
              </p>
            </div>
            <div className="flex justify-center">
              <Home className="w-5 h-5 text-slate-400 animate-pulse" />
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
